import React, { useState, useEffect, useMemo } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { themeCreator, modernThemeFor } from './base';
import { StylesProvider } from '@mui/styles';
import { DEFAULT_PALETTE_ID, DEFAULT_SURFACE, ModernSurface } from './modernPalettes';

export const ThemeContext = React.createContext(
  (themeName: string): void => {}
);

// Company "Full Modern" look, read from the same sessionStorage companySettings blob
// DefaultPOSTerminal lives in:
//   AppTheme        'classic' | 'modern'
//   AppThemeColor   palette id (violet, ocean, sunset, emerald, royal, cherry, graphite,
//                   aurora, frost)
//   AppThemeSurface 'light'   → light canvas with dark chrome
//                   'gradient'→ fully immersive gradient canvas (sidebar + header + body)
interface CompanyLook {
  modern: boolean;
  palette: string;
  surface: ModernSurface;
}

const readCompanyLook = (): CompanyLook => {
  try {
    const raw = sessionStorage.getItem('companySettings');
    if (!raw) return { modern: false, palette: DEFAULT_PALETTE_ID, surface: DEFAULT_SURFACE };
    const s = JSON.parse(raw) as Record<string, any>;
    const cs = s.customSettings ?? s.CustomSettings;
    const theme = String(cs?.AppTheme ?? cs?.appTheme ?? '').toLowerCase();
    const palette = String(cs?.AppThemeColor ?? cs?.appThemeColor ?? '').trim();
    const surface = String(cs?.AppThemeSurface ?? cs?.appThemeSurface ?? '').toLowerCase();
    return {
      modern: theme === 'modern',
      palette: palette || DEFAULT_PALETTE_ID,
      // An explicit choice always wins; only an unset value falls back to the default.
      surface: surface === 'light' ? 'light' : surface === 'gradient' ? 'gradient' : DEFAULT_SURFACE
    };
  } catch {
    return { modern: false, palette: DEFAULT_PALETTE_ID, surface: DEFAULT_SURFACE };
  }
};

const ThemeProviderWrapper: React.FC = (props) => {
  const curThemeName = localStorage.getItem('appTheme') || 'PureLightTheme';
  const [themeName, _setThemeName] = useState(curThemeName);
  const [look, setLook] = useState<CompanyLook>(readCompanyLook);

  // Re-resolve when login stores companySettings or the settings/developer pages change
  // the look ('appThemeChanged'), and on cross-tab changes ('storage').
  useEffect(() => {
    const handler = () => setLook(readCompanyLook());
    window.addEventListener('appThemeChanged', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('appThemeChanged', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);

  // Under Full Modern the light/dark toggle still works. 'gradient' surface is the
  // immersive look (whole page = the dashboard-style gradient), so it forces dark.
  const theme = useMemo(() => {
    if (!look.modern) return themeCreator(themeName);
    const dark = look.surface === 'gradient' || themeName === 'DarkTheme';
    return modernThemeFor(look.palette, dark);
  }, [look, themeName]);

  const setThemeName = (themeName: string): void => {
    localStorage.setItem('appTheme', themeName);
    _setThemeName(themeName);
  };

  // Apply theme immediately on mount and when it changes
  useEffect(() => {
    // Modern schemes paint a gradient canvas via CssBaseline and set background.default
    // to 'transparent' — stamping an inline colour here would flatten it, so skip.
    const bg = theme.palette.background.default;
    document.body.style.backgroundColor = bg === 'transparent' ? '' : bg;
    document.body.style.color = theme.palette.text.primary;
  }, [theme]);

  return (
    <StylesProvider injectFirst>
      <ThemeContext.Provider value={setThemeName}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {props.children}
        </ThemeProvider>
      </ThemeContext.Provider>
    </StylesProvider>
  );
};

export default ThemeProviderWrapper;
